import React, { useState } from 'react';
import Input from '../components/Input';
import { useAuth } from '../store/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './Checkout.module.css';
import Card from '../components/Card';
import Button from '../components/Button';
import { paymentService } from '../services/api';
import { useToast } from '../store/ToastContext';
import { formatCurrency, GST_RATE, calculateGst } from '../utils/helpers';

function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  
  const { plan, dongleCount, priceBreakdown } = location.state || {};
  const { user, updateUser } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [taxDetails, setTaxDetails] = useState({ gstin: user?.gstin || '', pan: user?.pan || '' });
  const [taxError, setTaxError] = useState('');

  if (!plan || !priceBreakdown) {
    navigate('/plans');
    return null;
  }

  const gstBreakup = calculateGst(priceBreakdown.total);

  const handleTaxChange = (e) => {
    const { name, value } = e.target;
    setTaxDetails((prev) => ({ ...prev, [name]: value.toUpperCase() }));
    setTaxError('');
  };

  // At least one of GSTIN / PAN is required to pay
  const validateTaxDetails = () => {
    const gstin = taxDetails.gstin.trim();
    const pan = taxDetails.pan.trim();
    if (!gstin && !pan) return 'Please enter your GSTIN or PAN to continue';
    if (gstin && !/^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(gstin)) return 'Please enter a valid 15-character GSTIN';
    if (pan && !/^[A-Z]{5}\d{4}[A-Z]$/.test(pan)) return 'Please enter a valid 10-character PAN (e.g. ABCDE1234F)';
    return '';
  };

  const handlePayment = async () => {
    const validationError = validateTaxDetails();
    if (validationError) {
      setTaxError(validationError);
      return;
    }
    setProcessing(true);

    try {
      const gstin = taxDetails.gstin.trim();
      const pan = taxDetails.pan.trim();
      // Keep the profile in sync so the new values show on the Profile page
      if (gstin !== (user?.gstin || '') || pan !== (user?.pan || '')) {
        await updateUser({ gstin, pan });
      }

      // Create order
      const orderResponse = await paymentService.createOrder({
        plan_id: plan.id,
        number_of_dongles: dongleCount,
        gstin,
        pan,
      });
      
      const orderData = orderResponse.data.data || orderResponse.data;
      const { 
        order_id,
        amount,
        amount_in_paise,
        currency, 
        razorpay_key_id, 
        order_number,
        user: orderUser
      } = orderData;

      // Initialize Razorpay
      const options = {
        key: razorpay_key_id,
        amount: amount_in_paise, // Already in paise from backend
        currency: currency || 'INR',
        name: 'DSC Platform',
        description: `${plan.name} Plan - ${dongleCount} Dongle(s)`,
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment
            await paymentService.verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            
            navigate('/payment-success', {
              state: {
                transactionId: response.razorpay_payment_id,
                orderNumber: order_number,
                plan: plan.name,
                amount: amount ?? gstBreakup.total,
                dongleCount,
              },
            });
          } catch (error) {
            navigate('/payment-failed', {
              state: { error: 'Payment verification failed' },
            });
          }
        },
        prefill: {
          name: orderUser?.name || '',
          email: orderUser?.email || '',
          contact: orderUser?.phone || '',
        },
        theme: {
          color: '#2563eb',
        },
        modal: {
          ondismiss: function () {
            setProcessing(false);
          },
        },
      };

      // Check if Razorpay SDK is loaded
      if (!window.Razorpay) {
        toast.error('Payment gateway not loaded. Please refresh the page.');
        setProcessing(false);
        return;
      }

      const razorpay = new window.Razorpay(options);
      
      razorpay.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        navigate('/payment-failed', {
          state: { error: response.error?.description || 'Payment failed' },
        });
      });
      
      razorpay.open();
      
    } catch (error) {
      console.error('Payment initiation error:', error);
      const message = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to initiate payment';
      toast.error(message);
      setProcessing(false);
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Checkout</h1>
        <p className={styles.subtitle}>Review your order and complete payment</p>
      </header>

      <div className={styles.content}>
        {/* Order Summary */}
        <Card title="Order Summary">
          <div className={styles.orderDetails}>
            <div className={styles.orderRow}>
              <span className={styles.orderLabel}>Plan</span>
              <span className={styles.orderValue}>{plan.name}</span>
            </div>
            <div className={styles.orderRow}>
              <span className={styles.orderLabel}>Duration</span>
              <span className={styles.orderValue}>{plan.duration_days} days</span>
            </div>
            <div className={styles.orderRow}>
              <span className={styles.orderLabel}>Number of Licences</span>
              <span className={styles.orderValue}>{dongleCount}</span>
            </div>
            
            <div className={styles.divider} />
            
            <div className={styles.orderRow}>
              <span className={styles.orderLabel}>Base Licence Price</span>
              <span className={styles.orderValue}>{formatCurrency(priceBreakdown.base_price)}</span>
            </div>
            {priceBreakdown.dongle_price > 0 && (
              <div className={styles.orderRow}>
                <span className={styles.orderLabel}>Additional Licences</span>
                <span className={styles.orderValue}>{formatCurrency(priceBreakdown.dongle_price)}</span>
              </div>
            )}
            
            <div className={styles.orderRow}>
              <span className={styles.orderLabel}>Subtotal</span>
              <span className={styles.orderValue}>{formatCurrency(gstBreakup.subtotal)}</span>
            </div>
            <div className={styles.orderRow}>
              <span className={styles.orderLabel}>GST ({GST_RATE}%)</span>
              <span className={styles.orderValue}>{formatCurrency(gstBreakup.gst)}</span>
            </div>

            <div className={styles.divider} />

            <div className={`${styles.orderRow} ${styles.totalRow}`}>
              <span className={styles.orderLabel}>Total (incl. GST)</span>
              <span className={styles.totalAmount}>{formatCurrency(gstBreakup.total)}</span>
            </div>
          </div>
        </Card>

        {/* Tax details: GSTIN or PAN is mandatory */}
        <Card title="Tax Details">
          <div className={styles.taxDetails}>
            <p className={styles.taxHint}>Provide at least one of GSTIN or PAN to continue.</p>
            <Input
              label="GSTIN"
              name="gstin"
              value={taxDetails.gstin}
              onChange={handleTaxChange}
              placeholder="15-character GSTIN"
              maxLength={15}
            />
            <Input
              label="PAN"
              name="pan"
              value={taxDetails.pan}
              onChange={handleTaxChange}
              placeholder="10-character PAN, e.g. ABCDE1234F"
              maxLength={10}
              error={taxError}
            />
          </div>
        </Card>

        {/* Payment Section */}
        <Card title="Payment">
          <div className={styles.paymentSection}>
            <p className={styles.paymentInfo}>
              You will be redirected to Razorpay's secure payment gateway to complete your purchase.
            </p>
            <div className={styles.paymentMethods}>
              <img src="https://cdn.razorpay.com/static/assets/logo/payment.svg" alt="Payment Methods" className={styles.paymentLogo} />
            </div>
            <div className={styles.paymentActions}>
              <Button variant="secondary" onClick={() => navigate('/plans')}>
                Back to Plans
              </Button>
              <Button onClick={handlePayment} loading={processing}>
                Pay {formatCurrency(gstBreakup.total)}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default Checkout;
